import React, {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {useTranslation} from "react-i18next";

import {LessonDetailsResponse} from "../types";
import {getLessonByIdAndCurrentUser} from "../api/api";
import {useAlert} from "../context/AlertContext";
import BreadcrumbLink from "../components/BreadcrumbLink";

const MyLessonDetailsPage = () => {
    const {t} = useTranslation();

    const {lessonId} = useParams<{ lessonId?: string }>();
    const safeLessonId = lessonId ?? "";

    const [lesson, setLesson] = useState<LessonDetailsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const {showAlert} = useAlert();

    useEffect(() => {
        if (!safeLessonId) return;

        let cancelled = false;

        setLoading(true);
        setHasError(false);

        const id = Number(safeLessonId);

        getLessonByIdAndCurrentUser(id)
            .then((res) => {
                if (!cancelled) setLesson(res);
            })
            .catch((error: any) => {
                if (cancelled) return;

                setHasError(true);
                setLesson(null);
                showAlert(error?.message ?? t("errors.contactAdmins"), "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeLessonId]);

    const handleRetry = () => {
        if (!safeLessonId) return;

        setHasError(false);
        setLoading(true);

        const id = Number(safeLessonId);

        getLessonByIdAndCurrentUser(id)
            .then((res) => setLesson(res))
            .catch((error: any) => {
                setHasError(true);
                setLesson(null);
                showAlert(
                    error?.message ?? t("errors.contactAdmins"),
                    "error"
                );
            })
            .finally(() => setLoading(false));
    };

    if (loading && !lesson) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                <Typography color="text.secondary">
                    {t("mylessondetails.errors.loadFailed")}
                </Typography>

                <Box sx={{display: "flex", gap: 2}}>
                    <Button
                        variant="contained"
                        onClick={handleRetry}
                        disabled={!safeLessonId}
                    >
                        {t("common.retry")}
                    </Button>

                    <Button component={Link} to="/my-courses" variant="outlined">
                        {t("common.backToMyCourses")}
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!lesson) return null;

    const isLecture = (lesson.lessonContentType ?? "LECTURE") === "LECTURE";

    return (
        <Box
            sx={{
                pb: 3,
                px: {xs: 3, md: 3},
            }}
        >
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={2}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/my-courses">
                    {t("breadcrumb.myCourses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course-details/${lesson.courseId}`}>
                    {lesson.courseTitle || t("breadcrumb.courseDetails")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter-details/${lesson.chapterId}`}>
                    {lesson.chapterTitle || t("breadcrumb.chapterDetails")}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{lesson.title || t("breadcrumb.lessonDetails")}</strong>
                </Typography>
            </Breadcrumbs>

            {isLecture && (
                <Box sx={{mb: 3}}>
                    <Tabs
                        value="lesson"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 48,
                            px: 0,
                            border: 0,
                            backgroundColor: "transparent",

                            "& .MuiTabs-scroller": {
                                border: 0,
                            },

                            "& .MuiTabs-flexContainer": {
                                gap: 1,
                            },

                            "& .MuiTabs-indicator": {
                                height: 3,
                                borderRadius: "3px 3px 0 0",
                            },

                            "& .MuiTab-root": {
                                minHeight: 48,
                                minWidth: "auto",
                                px: 2.5,
                                py: 1,
                                border: 0,
                                borderRadius: 0,
                                boxShadow: "none",
                                fontWeight: 700,
                                textTransform: "none",
                                color: "text.secondary",
                                backgroundColor: "transparent",

                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },

                                "&.Mui-selected": {
                                    color: "primary.main",
                                },

                                "&.Mui-focusVisible": {
                                    outline: "none",
                                },
                            },
                        }}
                    >
                        <Tab
                            value="lesson"
                            label={t("tabs.lesson")}
                            component={Link}
                            to={`/lesson-details/${lesson.id}`}
                        />

                        <Tab
                            value="code-tasks"
                            label={t("tabs.codeTasks")}
                            component={Link}
                            to={`/lesson-details/${lesson.id}/tasks`}
                        />

                        <Tab
                            value="practice-tasks"
                            label={t("tabs.practiceTasks")}
                            component={Link}
                            to={`/lesson-details/${lesson.id}/practices`}
                        />
                    </Tabs>
                </Box>
            )}

            <Box
                sx={{
                    minWidth: 0,
                    "& pre": {
                        p: 1.5,
                        borderRadius: 1,
                        overflow: "auto",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper !important",
                    },
                    "& pre[style]": {
                        background: "transparent !important",
                    },
                    "& code": {
                        fontFamily: "monospace",
                        fontSize: 14,
                    },
                }}
                dangerouslySetInnerHTML={{__html: lesson.content}}
            />
        </Box>
    );
};

export default MyLessonDetailsPage;